import { useEffect, useMemo, useState, useCallback, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import { setStaffCookie } from "../lib/staffCookie";
import StaffShell from "../components/staff/StaffShell";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  Copy,
  RefreshCw,
  XCircle,
  Loader2,
  ExternalLink,
  Plus,
  Globe,
  Users,
  CheckCircle2,
  Eye,
  EyeOff,
  Trophy,
  Sparkles,
  AlertCircle,
  ClipboardList,
  Trash2,
  Check,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CURRENCIES = ["GBP", "EUR", "USD", "AUD", "CAD", "JPY", "CHF"];

const STATUS_META = {
  minted: { label: "Minted", tone: "stone" },
  sent: { label: "Sent", tone: "sky" },
  opened: { label: "Opened", tone: "indigo" },
  drafted: { label: "Drafting", tone: "amber" },
  submitted: { label: "Submitted", tone: "emerald" },
  revoked: { label: "Revoked", tone: "rose" },
  expired: { label: "Expired", tone: "rose" },
};

// ---- Brief-diff helpers --------------------------------------------------
// Word-level coverage check: for each sentence in the original Cognito
// brief, we compute how many of its "significant" words still appear in
// the staff-editable paraphrase. Sentences with low coverage are
// highlighted amber so staff can spot anything Claude may have dropped
// (drug names, sample qualifiers, screening criteria etc.) at a glance.
const STOPWORDS = new Set([
  "the", "and", "a", "an", "of", "to", "in", "for", "on", "by", "with",
  "is", "are", "was", "were", "be", "been", "being", "have", "has", "had",
  "do", "does", "did", "this", "that", "these", "those", "it", "its",
  "as", "at", "or", "if", "but", "not", "no", "we", "you", "they", "i",
  "from", "into", "any", "all", "our", "your", "their", "his", "her",
  "will", "would", "should", "could", "can", "may", "shall", "than",
  "so", "such", "more", "less", "some", "each", "per", "also", "very",
  "etc", "eg", "ie", "via", "around", "about", "between", "during",
]);

function _tokenize(text) {
  return (text || "")
    .toLowerCase()
    .split(/[^a-z0-9'-]+/)
    .filter((w) => w.length > 2 && !STOPWORDS.has(w));
}

function _splitIntoSentences(paragraph) {
  // Naive sentence splitter, keeps full-stops / question marks /
  // exclamation marks as delimiters. Good enough for diff visualisation.
  return (paragraph || "")
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+(?=[A-Z(])/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * For each original sentence, return:
 *   { text, coverage: 0..1, missingTokens: string[] }
 * ``paraphraseTokens`` is a Set of every significant word in the
 * staff-editable textarea (case-folded, deduped) so the per-sentence
 * lookup is O(words).
 */
function _diffOriginal(originalParagraphs, paraphraseText) {
  const paraphraseSet = new Set(_tokenize(paraphraseText));
  return (originalParagraphs || []).map((para) => {
    const sentences = _splitIntoSentences(para).map((s) => {
      const toks = _tokenize(s);
      if (toks.length === 0) return { text: s, coverage: 1, missing: [] };
      const missing = toks.filter((t) => !paraphraseSet.has(t));
      const coverage = (toks.length - missing.length) / toks.length;
      return { text: s, coverage, missing };
    });
    return sentences;
  });
}

const TONE_CLS = {
  stone: "bg-stone-500/15 text-stone-400 border-stone-500/30",
  sky: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  indigo: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rose: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

/* Format a JS Date as a ``<input type="datetime-local">`` string in
   the browser's local timezone. */
function _toDatetimeLocalString(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/* Default mint deadline: N days from now at HH:00 local. */
function _defaultExpiresAtLocal(days, hour) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  return _toDatetimeLocalString(d);
}

/* Minimum allowed value for the datetime-local picker, 5 minutes
   from now so staff can't tab past validation by entering "now". */
function _minExpiresAtLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 5);
  return _toDatetimeLocalString(d);
}

export default function StaffRfqs() {
  const { entry } = useParams();
  const entryNumber = Number.parseInt(entry, 10);
  const [proposal, setProposal] = useState(null);
  const [proposalErr, setProposalErr] = useState(null);
  const [senders, setSenders] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [overlay, setOverlay] = useState({ active: false, picks: {} });
  const [resolvedOverlay, setResolvedOverlay] = useState({ resolved: {} });
  const [loading, setLoading] = useState(true);
  const [showMintForm, setShowMintForm] = useState(false);
  const [minting, setMinting] = useState(false);
  // Mint form state. ``expires_at`` is a ``datetime-local`` string in
  // the staff member's browser timezone (e.g. "2026-06-24T17:00").
  // We default to 14 days from now at 17:00 local, the previous
  // "14 days from now" default rounded to an end-of-day deadline.
  const [form, setForm] = useState(() => ({
    supplier_name: "",
    supplier_email: "",
    supplier_company: "",
    sender_email: "",
    countries: [],
    currency: "GBP",
    expires_at: _defaultExpiresAtLocal(14, 17),
    extra_notes: "",
  }));
  const [newCountry, setNewCountry] = useState("");
  // Overlay picks state, { "country::service": rfq_token }
  const [picks, setPicks] = useState({});
  // Editable supplier-facing brief paragraphs. Fetched lazily when
  // the mint form opens (LLM round-trip is 5-30s, so we don't want it
  // on page load), and kept as a newline-separated textarea string so
  // staff can freely edit / split / merge paragraphs. Split by
  // double-newline on submit. ``originalParagraphs`` are the raw
  // (verbatim) Cognito study specs, used by the side-by-side diff
  // panel so staff can spot any operational detail Claude may have
  // dropped during the paraphrase.
  const [briefText, setBriefText] = useState("");
  const [originalParagraphs, setOriginalParagraphs] = useState([]);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState(null);
  const [briefGeneratedAt, setBriefGeneratedAt] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);

  // ---- Cost-grid validation state -----------------------------------
  // The mint form preloads the supplier-facing cost grid into a deep
  // mutable copy (``editedGrid``). Every field on every row is
  // editable inline (label, sample, units, rates, captions); rows can
  // be deleted; custom rows can be appended per section. The merged
  // ``editedGrid`` is POSTed verbatim as ``grid_override`` on submit.
  const [gridBase, setGridBase] = useState([]); // Source-of-truth from /grid-preview
  const [editedGrid, setEditedGrid] = useState([]); // Staff edits
  const [gridLoading, setGridLoading] = useState(false);
  const [gridError, setGridError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [rfqsRes, overlayRes, resolvedRes] = await Promise.all([
        axios.get(`${API}/api/proposals/${entryNumber}/supplier-rfqs`),
        axios.get(
          `${API}/api/proposals/${entryNumber}/supplier-cost-overlay`
        ),
        axios.get(
          `${API}/api/proposals/${entryNumber}/supplier-cost-overlay/resolved`
        ),
      ]);
      setRfqs(rfqsRes.data.rfqs || []);
      setOverlay(overlayRes.data || { active: false, picks: {} });
      setPicks(overlayRes.data?.picks || {});
      setResolvedOverlay(resolvedRes.data || { resolved: {} });
    } catch (e) {
      toast.error("Could not refresh RFQs.");
    }
  }, [entryNumber]);

  // Initial load, project info + lookups + RFQ list
  useEffect(() => {
    // Mark this browser as staff so any later /p/{token} open from
    // it is excluded from client-facing analytics automatically.
    setStaffCookie();
    let cancelled = false;
    (async () => {
      setLoading(true);
      setProposalErr(null);
      try {
        // Mint/lookup share token to fetch project info.
        const { data: link } = await axios.post(
          `${API}/api/proposals/share`,
          { entry_number: entryNumber }
        );
        if (cancelled) return;
        const { data: pub } = await axios.get(
          `${API}/api/public/proposals/${encodeURIComponent(link.token)}`
        );
        if (cancelled) return;
        setProposal({ ...pub, share_token: link.token });
        // Pre-fill mint form with project's countries
        const projCountries = pub.countries || [];
        setForm((f) => ({ ...f, countries: projCountries }));

        // Load lookups + RFQs in parallel
        const [s, dir, rfqsRes, overlayRes, resolvedRes] =
          await Promise.all([
            axios.get(`${API}/api/proposals/supplier-rfqs/senders`),
            axios.get(`${API}/api/proposals/supplier-rfqs/directory`),
            axios.get(`${API}/api/proposals/${entryNumber}/supplier-rfqs`),
            axios.get(
              `${API}/api/proposals/${entryNumber}/supplier-cost-overlay`
            ),
            axios.get(
              `${API}/api/proposals/${entryNumber}/supplier-cost-overlay/resolved`
            ),
          ]);
        if (cancelled) return;
        setSenders(s.data.senders || []);
        setDirectory(dir.data.suppliers || []);
        setRfqs(rfqsRes.data.rfqs || []);
        setOverlay(overlayRes.data || { active: false, picks: {} });
        setPicks(overlayRes.data?.picks || {});
        setResolvedOverlay(resolvedRes.data || { resolved: {} });
        // Default sender = Frank (first in list) for now
        setForm((f) => ({
          ...f,
          sender_email:
            f.sender_email || s.data.senders?.[0]?.email || "",
        }));
      } catch (e) {
        setProposalErr(
          e?.response?.data?.detail ||
            "Could not load proposal, check the number."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entryNumber]);

  const toggleCountry = (c) => {
    setForm((f) => ({
      ...f,
      countries: f.countries.includes(c)
        ? f.countries.filter((x) => x !== c)
        : [...f.countries, c],
    }));
  };

  const addCountryFreeform = () => {
    const c = (newCountry || "").trim();
    if (!c) return;
    if (!form.countries.includes(c)) {
      setForm((f) => ({ ...f, countries: [...f.countries, c] }));
    }
    setNewCountry("");
  };

  const onPickSupplierFromDirectory = (s) => {
    setForm((f) => ({
      ...f,
      supplier_name: s.name || "",
      supplier_email: s.email || "",
      supplier_company: s.company || "",
    }));
  };

  const submitMint = async (e) => {
    e.preventDefault();
    if (!form.supplier_email || !form.supplier_name || !form.sender_email) {
      toast.error("Supplier name, email, and sender are required.");
      return;
    }
    if (form.countries.length === 0) {
      toast.error("Pick at least one country.");
      return;
    }
    // Validate the deadline is a parseable datetime in the future.
    // browsers don't always enforce ``min`` on type=datetime-local.
    const expiresAtDate = form.expires_at ? new Date(form.expires_at) : null;
    if (!expiresAtDate || Number.isNaN(expiresAtDate.getTime())) {
      toast.error("Please pick a valid deadline date & time.");
      return;
    }
    if (expiresAtDate.getTime() <= Date.now()) {
      toast.error("Deadline must be in the future.");
      return;
    }
    setMinting(true);
    try {
      // Split the textarea by blank-line into paragraphs.
      const briefParagraphs = briefText
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean);
      // Build the staff-validated cost grid (exclusions + custom rows
      // applied) so the backend stores exactly what the supplier will
      // be asked to quote, no auto-extracted leftovers.
      const finalGrid = buildFinalGrid();
      if (!finalGrid || finalGrid.length === 0) {
        toast.error(
          "Cost grid is empty, include at least one row or wait for the grid preview to load."
        );
        setMinting(false);
        return;
      }
      // Strip the local-time string from ``form`` and send an ISO UTC
      // timestamp instead, the backend interprets naive datetimes as
      // UTC, so always normalise here before transit.
      const { expires_at: _drop, ...rest } = form;
      const { data } = await axios.post(
        `${API}/api/proposals/${entryNumber}/supplier-rfqs`,
        {
          ...rest,
          expires_at: expiresAtDate.toISOString(),
          brief_override: briefParagraphs,
          grid_override: finalGrid,
        },
        { timeout: 60000 }
      );
      if (data.email_error) {
        toast.warning(
          `RFQ created but email failed: ${data.email_error}. You can copy the link manually.`
        );
      } else {
        toast.success("RFQ sent, supplier will receive an email shortly.");
      }
      setShowMintForm(false);
      // Reset mutable fields, keep proposal-wide defaults
      setForm((f) => ({
        ...f,
        supplier_name: "",
        supplier_email: "",
        supplier_company: "",
        extra_notes: "",
        // Re-roll the default deadline so the next mint starts from
        // "now + 14d @ 17:00 local" rather than the stale value.
        expires_at: _defaultExpiresAtLocal(14, 17),
      }));
      await refresh();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not send RFQ, please try again."
      );
    } finally {
      setMinting(false);
    }
  };

  // Load the LLM-paraphrased brief, runs the first time the mint
  // form opens, with refresh=true to force a fresh Claude pass. The
  // backend caches per-proposal so repeat opens are instant.
  const loadParaphrasedBrief = useCallback(
    async (refresh = false) => {
      setBriefLoading(true);
      setBriefError(null);
      try {
        const url = `${API}/api/proposals/${entryNumber}/supplier-rfqs/paraphrased-brief`;
        const { data } = refresh
          ? await axios.post(url, { refresh: true }, { timeout: 60000 })
          : await axios.get(url, { timeout: 60000 });
        const paragraphs = data?.paragraphs || [];
        setBriefText(paragraphs.join("\n\n"));
        setOriginalParagraphs(data?.original_paragraphs || []);
        setBriefGeneratedAt(data?.generated_at || null);
      } catch (e) {
        setBriefError(
          e?.response?.data?.detail ||
            "Could not generate the supplier brief. Try again or write one manually."
        );
      } finally {
        setBriefLoading(false);
      }
    },
    [entryNumber]
  );

  // Auto-load the brief the first time the mint form opens.
  useEffect(() => {
    if (showMintForm && !briefText && !briefLoading && !briefError) {
      void loadParaphrasedBrief(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMintForm]);

  // Load the cost grid the supplier will be asked to quote.
  // Fires whenever the mint form is open AND the country selection
  // changes (the grid is country-dependent because Cognito phases can
  // be country-scoped). Replaces ``editedGrid`` with a fresh deep
  // copy so any in-flight edits to a stale row_key set are dropped.
  const loadGridPreview = useCallback(async () => {
    if (!showMintForm || form.countries.length === 0) {
      setGridBase([]);
      setEditedGrid([]);
      return;
    }
    setGridLoading(true);
    setGridError(null);
    try {
      const params = new URLSearchParams();
      form.countries.forEach((c) => params.append("countries", c));
      const { data } = await axios.get(
        `${API}/api/proposals/${entryNumber}/supplier-rfqs/grid-preview?${params.toString()}`,
        { timeout: 30000 }
      );
      const phases = data.phases || [];
      setGridBase(phases);
      // Deep-clone so staff edits never mutate the base (we keep base
      // around in case we ever want to add a "reset to original" cue).
      setEditedGrid(JSON.parse(JSON.stringify(phases)));
    } catch (e) {
      setGridError(
        e?.response?.data?.detail || "Could not load the supplier cost grid."
      );
      setGridBase([]);
      setEditedGrid([]);
    } finally {
      setGridLoading(false);
    }
  }, [showMintForm, entryNumber, form.countries]);

  // Reload the grid when the mint form opens OR when the staff member
  // picks a different country set.
  useEffect(() => {
    void loadGridPreview();
  }, [loadGridPreview]);

  // ----- Mutators on editedGrid (immutable updates via deep clone) -----
  const _cloneGrid = (g) => JSON.parse(JSON.stringify(g));
  const updateGridRow = (pi, si, ri, patch) => {
    setEditedGrid((prev) => {
      const next = _cloneGrid(prev);
      const row = next[pi]?.sections?.[si]?.rows?.[ri];
      if (!row) return prev;
      next[pi].sections[si].rows[ri] = { ...row, ...patch };
      return next;
    });
  };
  const removeGridRow = (pi, si, ri) => {
    setEditedGrid((prev) => {
      const next = _cloneGrid(prev);
      if (!next[pi]?.sections?.[si]?.rows) return prev;
      next[pi].sections[si].rows.splice(ri, 1);
      return next;
    });
  };
  const addGridRow = (pi, si, row) => {
    setEditedGrid((prev) => {
      const next = _cloneGrid(prev);
      if (!next[pi]?.sections?.[si]) return prev;
      next[pi].sections[si].rows.push(row);
      return next;
    });
  };

  // The "final" grid is just ``editedGrid`` with empty sections /
  // phases dropped, the backend validator does this anyway, but a
  // local pass gives us a friendlier "grid is empty" guard before we
  // hit the network.
  const buildFinalGrid = useCallback(() => {
    if (!editedGrid || editedGrid.length === 0) return null;
    const out = [];
    editedGrid.forEach((phase) => {
      const sections = (phase.sections || []).filter((s) => {
        const rows = s.rows || [];
        if (rows.length === 0) return false;
        return rows.some((r) => r.kind !== "subgroup_header");
      });
      if (sections.length > 0) {
        out.push({
          phase_index: phase.phase_index,
          phase_name: phase.phase_name,
          countries: phase.countries,
          sections,
        });
      }
    });
    return out.length > 0 ? out : null;
  }, [editedGrid]);

  // Per-sentence diff between the raw Cognito study specs and the
  // staff-editable paraphrase. Recomputed cheaply on every keystroke.
  const originalDiff = useMemo(
    () => _diffOriginal(originalParagraphs, briefText),
    [originalParagraphs, briefText]
  );

  const copyLink = async (url) => {
    try {
      // Primary path, works in modern secure contexts.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied.");
        return;
      }
      throw new Error("Clipboard API unavailable");
    } catch {
      // Fallback for older browsers / non-HTTPS / sandboxed iframes.
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success("Link copied.");
        else toast.error("Could not copy, please copy manually.");
      } catch {
        toast.error("Could not copy, please copy manually.");
      }
    }
  };

  const resendRfq = async (token) => {
    try {
      await axios.post(
        `${API}/api/proposals/${entryNumber}/supplier-rfqs/${token}/resend`
      );
      toast.success("Reminder sent.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend.");
    }
  };

  const revokeRfq = async (token) => {
    if (
      !window.confirm(
        "Revoke this RFQ? The supplier's link will stop working immediately."
      )
    )
      return;
    try {
      await axios.post(
        `${API}/api/proposals/${entryNumber}/supplier-rfqs/${token}/revoke`
      );
      toast.success("Revoked.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke.");
    }
  };

  const setPick = async (cellKey, token) => {
    const next = { ...picks };
    if (!token) delete next[cellKey];
    else next[cellKey] = token;
    setPicks(next);
    try {
      await axios.post(
        `${API}/api/proposals/${entryNumber}/apply-supplier-costs`,
        {
          picks: Object.entries(next).map(([k, t]) => ({
            cell_key: k,
            token: t,
          })),
        }
      );
      // Refresh resolved overlay to recompute the GBP values shown in
      // the staff comparison matrix.
      const { data } = await axios.get(
        `${API}/api/proposals/${entryNumber}/supplier-cost-overlay/resolved`
      );
      setResolvedOverlay(data);
    } catch (e) {
      toast.error("Could not save pick.");
    }
  };

  const toggleOverlayActive = async () => {
    const next = !overlay.active;
    try {
      await axios.post(
        `${API}/api/proposals/${entryNumber}/supplier-cost-overlay/active`,
        { active: next }
      );
      setOverlay((o) => ({ ...o, active: next }));
      toast.success(
        next
          ? "Overlay is now visible to the client."
          : "Overlay hidden, client will see the original costs."
      );
    } catch (e) {
      toast.error("Could not toggle overlay.");
    }
  };

  // Build the comparison matrix, country × service rows, with one
  // column per supplier showing their submitted/draft per-unit cost
  // converted to GBP via the resolved-overlay endpoint.
  const submittedRfqs = useMemo(
    () =>
      rfqs.filter(
        (r) => r.submission || r.draft
      ),
    [rfqs]
  );

  // Union of every cell any supplier has quoted on (new shape: keys
  // are ``{row_key}:{country}`` mirroring the supplier-submission
  // shape, e.g. "p0:s0:r2:UK"). Sorted so the matrix renders rows in
  // the same order across renders.
  const matrixCells = useMemo(() => {
    const cells = new Set();
    for (const r of submittedRfqs) {
      const sub = r.submission || r.draft || {};
      for (const key of Object.keys(sub.unit_overrides || {})) {
        cells.add(key);
      }
    }
    return Array.from(cells).sort();
  }, [submittedRfqs]);

  // Look up a row's label + block context (Haematologists, etc.)
  // from any RFQ's frozen quotable_grid, the structure is identical
  // for every supplier on the same proposal so we just take the first
  // submitted RFQ's snapshot.
  const gridLookup = useMemo(() => {
    const lookup = {};
    const sourceGrid =
      submittedRfqs[0]?.brief_snapshot?.quotable_grid || [];
    for (const phase of sourceGrid) {
      for (const section of phase.sections || []) {
        for (const row of section.rows || []) {
          lookup[row.row_key] = {
            label: row.label,
            block_label: row.block_label,
            block_subtitle: row.block_subtitle,
            phase_name: phase.phase_name,
            section_label: section.label,
            default_samples: row.default_samples || {},
          };
        }
      }
    }
    return lookup;
  }, [submittedRfqs]);

  // For a given RFQ + cell, return the per-unit cost in GBP (uses live
  // FX cached in resolvedOverlay; for non-winning bids we compute from
  // the submission directly here since the resolved map only shows
  // winners).
  const fmtGbp = (n) => `£${(n ?? 0).toFixed(2)}`;

  if (loading) {
    return (
      <StaffShell max="wide">
        <div className="flex items-center justify-center py-20 text-stone-500">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
        </div>
      </StaffShell>
    );
  }

  if (proposalErr) {
    return (
      <StaffShell max="wide">
        <div className="flex flex-col items-center justify-center text-slate-700 py-20">
          <p className="text-lg mb-3">{proposalErr}</p>
          <Link
            to="/share"
            className="text-[#1B4F8A] underline text-sm font-mono tracking-wide"
          >
            ← Back to Share workspace
          </Link>
        </div>
      </StaffShell>
    );
  }

  return (
    <StaffShell max="wide" bodyClass="!pt-6">
      <div className="font-sans text-slate-900" data-testid="staff-rfqs-root">
        <section className="mb-12">
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#3EA8DC] mb-2">
            Supplier RFQs · proposal #{entryNumber}
          </p>
          <h1
            className="text-3xl sm:text-4xl font-serif font-light text-slate-900 mb-1"
            data-testid="project-reference"
          >
            {proposal?.project_reference || `Proposal #${entryNumber}`}
          </h1>
          <p className="text-sm text-stone-400">
            {proposal?.client_company || "-"}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setShowMintForm((s) => !s)}
              data-testid="toggle-mint-form"
              className="inline-flex items-center gap-1.5 bg-[#3EA8DC] hover:bg-[#5BB8E5] text-[#0B1220] text-xs font-mono tracking-[0.1em] uppercase font-semibold px-4 py-2.5 rounded-lg transition-colors"
            >
              <Send className="w-3.5 h-3.5" />{" "}
              {showMintForm ? "Cancel" : "Send a new RFQ"}
            </button>
            {rfqs.length > 0 && (
              <span className="text-[11px] text-stone-500 font-mono tracking-wide">
                {rfqs.length} request{rfqs.length === 1 ? "" : "s"} so far
              </span>
            )}
          </div>
        </section>

        {/* MINT FORM (collapsible) */}
        {showMintForm && (
          <section
            className="mb-12 bg-stone-50 border border-stone-200 rounded-2xl p-6 animate-in fade-in slide-in-from-top-4 duration-300"
            data-testid="mint-form-section"
          >
            <form onSubmit={submitMint} className="space-y-6">
              <h2 className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2">
                Send a request
              </h2>

              {/* Sender */}
              <div>
                <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2">
                  Send as
                </p>
                <div className="flex flex-wrap gap-2">
                  {senders.map((s) => (
                    <button
                      key={s.email}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({ ...f, sender_email: s.email }))
                      }
                      data-testid={`sender-${s.email}`}
                      className={`text-xs font-mono tracking-wide px-3 py-2 rounded-lg border transition-colors ${
                        form.sender_email === s.email
                          ? "bg-[#3EA8DC]/20 border-[#3EA8DC]/60 text-[#3EA8DC]"
                          : "bg-white border-stone-200 text-slate-700 hover:border-stone-300"
                      }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Supplier */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2">
                    Supplier name
                  </label>
                  <input
                    type="text"
                    required
                    value={form.supplier_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, supplier_name: e.target.value }))
                    }
                    data-testid="supplier-name"
                    className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-[#3EA8DC]/60"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2">
                    Supplier email
                  </label>
                  <input
                    type="email"
                    required
                    value={form.supplier_email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, supplier_email: e.target.value }))
                    }
                    data-testid="supplier-email"
                    className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-[#3EA8DC]/60"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2">
                    Supplier company (optional)
                  </label>
                  <input
                    type="text"
                    value={form.supplier_company}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        supplier_company: e.target.value,
                      }))
                    }
                    className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-[#3EA8DC]/60"
                  />
                </div>
              </div>

              {/* Supplier directory typeahead */}
              {directory.length > 0 && (
                <div>
                  <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2">
                    Or pick from past suppliers
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {directory.slice(0, 12).map((s) => (
                      <button
                        key={s.email}
                        type="button"
                        onClick={() => onPickSupplierFromDirectory(s)}
                        className="text-[11px] font-mono tracking-wide px-2.5 py-1 rounded-md bg-white hover:bg-stone-100 border border-stone-200 text-slate-700 transition-colors"
                      >
                        {s.name || s.email}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Countries */}
              <div>
                <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2">
                  Countries in scope for this supplier
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.countries.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCountry(c)}
                      className="inline-flex items-center gap-1.5 bg-[#3EA8DC]/20 border border-[#3EA8DC]/60 text-[#3EA8DC] text-xs font-mono px-3 py-1.5 rounded-full"
                    >
                      <Globe className="w-3 h-3" /> {c}
                      <span className="opacity-60">×</span>
                    </button>
                  ))}
                  {(proposal?.countries || [])
                    .filter((c) => !form.countries.includes(c))
                    .map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleCountry(c)}
                        className="inline-flex items-center gap-1.5 bg-white hover:bg-stone-100 border border-stone-200 text-slate-700 text-xs font-mono px-3 py-1.5 rounded-full transition-colors"
                      >
                        <Plus className="w-3 h-3" /> {c}
                      </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newCountry}
                    onChange={(e) => setNewCountry(e.target.value)}
                    placeholder="Add another country…"
                    onKeyDown={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addCountryFreeform())
                    }
                    className="flex-1 bg-white border border-stone-200 rounded-md px-3 py-1.5 text-xs text-slate-900 focus:outline-none focus:border-[#3EA8DC]/60"
                  />
                  <button
                    type="button"
                    onClick={addCountryFreeform}
                    className="text-[11px] font-mono uppercase tracking-wide bg-white hover:bg-stone-100 border border-stone-200 px-3 py-1.5 rounded-md text-slate-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Currency + expiry */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2">
                    Default currency for supplier
                  </label>
                  <select
                    value={form.currency}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, currency: e.target.value }))
                    }
                    className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-[#3EA8DC]/60"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    className="block text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2"
                    htmlFor="mint-deadline"
                  >
                    Deadline (date & time)
                  </label>
                  <input
                    id="mint-deadline"
                    type="datetime-local"
                    data-testid="mint-expires-at"
                    min={_minExpiresAtLocal()}
                    value={form.expires_at}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        expires_at: e.target.value,
                      }))
                    }
                    className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-[#3EA8DC]/60 tabular-nums"
                  />
                  <p className="mt-1.5 text-[10px] text-stone-500 font-mono tracking-wide">
                    Local time,{" "}
                    {form.expires_at
                      ? new Date(form.expires_at).toLocaleString(undefined, {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                          timeZoneName: "short",
                        })
                      : "-"}
                  </p>
                </div>
              </div>

              {/* Extra notes */}
              <div>
                <label className="block text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-2">
                  Extra note for the supplier (optional)
                </label>
                <textarea
                  rows={2}
                  value={form.extra_notes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, extra_notes: e.target.value }))
                  }
                  className="w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-[#3EA8DC]/60 resize-vertical"
                  placeholder="e.g. happy to discuss feasibility on a call"
                />
              </div>

              {/* Anonymised supplier brief, LLM-paraphrased, staff-editable */}
              <div data-testid="brief-editor-section" className="bg-stone-50/80 border border-[#3EA8DC]/20 rounded-xl p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#3EA8DC] mb-1">
                      <Sparkles className="w-3 h-3 inline -mt-0.5 mr-1" />
                      Supplier brief · anonymised
                    </p>
                    <p className="text-[11px] text-stone-500 leading-relaxed">
                      Generated from the Cognito study specs and reworded so a supplier can&apos;t fingerprint our client. Edit freely, operational detail (drug names, sample, methodology) should stay; client identifiers should not.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {originalParagraphs.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowOriginal((v) => !v)}
                        data-testid="toggle-show-original"
                        className={`inline-flex items-center gap-1.5 text-[11px] font-mono tracking-[0.08em] uppercase transition-colors ${
                          showOriginal
                            ? "text-[#3EA8DC]"
                            : "text-stone-400 hover:text-[#3EA8DC]"
                        }`}
                      >
                        {showOriginal ? (
                          <EyeOff className="w-3 h-3" />
                        ) : (
                          <Eye className="w-3 h-3" />
                        )}
                        {showOriginal ? "Hide original" : "Show original"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => loadParaphrasedBrief(true)}
                      disabled={briefLoading}
                      data-testid="regenerate-brief"
                      className="inline-flex items-center gap-1.5 text-[11px] font-mono tracking-[0.08em] uppercase text-slate-700 hover:text-[#3EA8DC] disabled:opacity-50 disabled:cursor-wait transition-colors"
                    >
                      {briefLoading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3 h-3" />
                      )}
                      Regenerate
                    </button>
                  </div>
                </div>
                {briefError && (
                  <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2 flex items-start gap-2">
                    <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
                    <span>{briefError}</span>
                  </div>
                )}
                <div
                  className={
                    showOriginal
                      ? "grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch"
                      : ""
                  }
                >
                  {showOriginal && (
                    <div
                      data-testid="original-brief-panel"
                      className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 max-h-[420px] overflow-y-auto order-2 lg:order-1"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-amber-300/80">
                          Original Cognito specs
                        </p>
                        <p className="text-[9px] font-mono tracking-wider uppercase text-stone-400">
                          read-only · for comparison
                        </p>
                      </div>
                      <p className="text-[10px] text-stone-500 mb-3 leading-relaxed">
                        Sentences highlighted amber have low word-overlap with your paraphrase, check nothing operational was dropped.
                      </p>
                      <div className="space-y-3 font-serif text-sm leading-relaxed">
                        {originalDiff.map((sentences, pi) => (
                          <p key={pi}>
                            {sentences.map((s, si) => {
                              // Coverage <0.4 = likely dropped (amber);
                              // 0.4-0.75 = partly rewritten (subtle);
                              // >0.75 = mostly preserved (no decoration).
                              const tone =
                                s.coverage < 0.4
                                  ? "bg-amber-500/25 text-amber-100 px-0.5 rounded-sm"
                                  : s.coverage < 0.75
                                  ? "text-slate-700"
                                  : "text-stone-500";
                              const titleHint =
                                s.missing && s.missing.length
                                  ? `Missing in paraphrase: ${s.missing.slice(0, 8).join(", ")}${s.missing.length > 8 ? "…" : ""}`
                                  : "Preserved";
                              return (
                                <span key={si} className={tone} title={titleHint}>
                                  {s.text}{" "}
                                </span>
                              );
                            })}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className={showOriginal ? "order-1 lg:order-2" : ""}>
                    <textarea
                      rows={showOriginal ? 18 : 12}
                      value={briefText}
                      onChange={(e) => setBriefText(e.target.value)}
                      disabled={briefLoading}
                      data-testid="brief-textarea"
                      placeholder={
                        briefLoading
                          ? "Generating anonymised brief, Claude is rewording the study specs (5–30s)…"
                          : "The supplier-facing brief will be auto-generated when you open this form. You can edit it freely before sending."
                      }
                      className="w-full h-full min-h-[200px] bg-white border border-stone-200 rounded-lg px-3 py-3 text-sm text-slate-900 placeholder:text-stone-400 focus:outline-none focus:border-[#3EA8DC]/60 resize-vertical disabled:opacity-60 font-serif leading-relaxed"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-stone-400 font-mono tracking-wide">
                  Tip: separate paragraphs with a blank line.{" "}
                  {briefGeneratedAt && (
                    <>
                      Last regenerated{" "}
                      {new Date(briefGeneratedAt).toLocaleString(undefined, {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}.
                    </>
                  )}
                </p>
              </div>

              {/* Grid validation panel, staff sees the exact same
                  cost grid the supplier will fill in, with every
                  field editable inline. Rows can be deleted; new
                  ones added per section. */}
              <StaffGridEditor
                phases={editedGrid}
                loading={gridLoading}
                error={gridError}
                onRetry={loadGridPreview}
                currency={form.currency}
                updateRow={updateGridRow}
                removeRow={removeGridRow}
                addRow={addGridRow}
              />

              <button
                type="submit"
                disabled={
                  minting ||
                  briefLoading ||
                  !briefText.trim() ||
                  gridLoading ||
                  editedGrid.length === 0
                }
                data-testid="submit-mint"
                className="inline-flex items-center gap-2 bg-[#3EA8DC] hover:bg-[#5BB8E5] text-[#0B1220] text-xs font-mono tracking-[0.1em] uppercase font-semibold px-5 py-2.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {minting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                Send invite
              </button>
            </form>
          </section>
        )}

        {/* RFQ LIST */}
        <section className="mb-16">
          <h2 className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-4">
            <Users className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" />
            Supplier requests
          </h2>
          {rfqs.length === 0 ? (
            <div className="bg-stone-50 border border-stone-200 rounded-2xl p-8 text-center">
              <p className="text-sm text-stone-500 mb-1">
                No requests sent yet.
              </p>
              <p className="text-[11px] text-stone-400 font-mono tracking-wide">
                Click &quot;Send a new RFQ&quot; to invite your first supplier.
              </p>
            </div>
          ) : (
            <div className="bg-stone-50 border border-stone-200 rounded-2xl divide-y divide-stone-200 overflow-hidden">
              {rfqs.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.minted;
                const supplierUrl = `${window.location.origin}/q/${r.token}`;
                return (
                  <div
                    key={r.token}
                    className="p-4 sm:p-5 flex flex-wrap items-start justify-between gap-3"
                    data-testid={`rfq-row-${r.token}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {r.supplier_name}
                          {r.supplier_company && (
                            <span className="text-stone-500 ml-2 text-xs">
                              · {r.supplier_company}
                            </span>
                          )}
                        </p>
                        <span
                          className={`text-[10px] font-mono tracking-wide uppercase px-2 py-0.5 rounded border ${
                            TONE_CLS[meta.tone] || TONE_CLS.stone
                          }`}
                          data-testid={`status-${r.token}`}
                        >
                          {meta.label}
                        </span>
                        {(r.submission?.feasibility_status === "reduced_scope" ||
                          r.draft?.feasibility_status === "reduced_scope") && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-mono tracking-wide uppercase px-2 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/40"
                            data-testid={`reduced-scope-badge-${r.token}`}
                          >
                            ⚠ Reduced scope
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-stone-500 font-mono tracking-wide">
                        {r.supplier_email} · sent by{" "}
                        {r.sender_name || r.sender_email}
                      </p>
                      <p className="text-[11px] text-stone-400 font-mono tracking-wide mt-0.5">
                        {(r.countries || []).join(", ")}
                      </p>
                      <p className="text-[10px] text-stone-300 font-mono tracking-wide mt-0.5">
                        Due{" "}
                        {r.expires_at
                          ? new Date(r.expires_at).toLocaleString(undefined, {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                        {r.submitted_at
                          ? ` · submitted ${new Date(
                              r.submitted_at
                            ).toLocaleString(undefined, {
                              day: "numeric",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}`
                          : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => copyLink(supplierUrl)}
                        title="Copy supplier link"
                        data-testid={`copy-${r.token}`}
                        className="p-2 text-stone-400 hover:text-[#3EA8DC] hover:bg-white rounded-md transition-colors"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <a
                        href={supplierUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open as supplier"
                        data-testid={`open-${r.token}`}
                        className="p-2 text-stone-400 hover:text-[#3EA8DC] hover:bg-white rounded-md transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      {!["revoked", "expired", "submitted"].includes(
                        r.status
                      ) && (
                        <button
                          type="button"
                          onClick={() => resendRfq(r.token)}
                          title="Send reminder"
                          data-testid={`resend-${r.token}`}
                          className="p-2 text-stone-400 hover:text-amber-300 hover:bg-white rounded-md transition-colors"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!["revoked", "expired"].includes(r.status) && (
                        <button
                          type="button"
                          onClick={() => revokeRfq(r.token)}
                          title="Revoke"
                          data-testid={`revoke-${r.token}`}
                          className="p-2 text-stone-400 hover:text-rose-400 hover:bg-white rounded-md transition-colors"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* COMPARISON MATRIX */}
        {submittedRfqs.length > 0 && (
          <section
            className="mb-12"
            data-testid="comparison-matrix-section"
          >
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h2 className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500">
                <Trophy className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" />
                Cost comparison
              </h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={toggleOverlayActive}
                  data-testid="toggle-overlay-active"
                  className={`inline-flex items-center gap-1.5 text-[11px] font-mono tracking-[0.1em] uppercase px-3 py-2 rounded-lg border transition-colors ${
                    overlay.active
                      ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300"
                      : "bg-white border-stone-200 text-stone-400 hover:border-stone-300"
                  }`}
                >
                  {overlay.active ? (
                    <>
                      <Eye className="w-3 h-3" /> Overlay live to client
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-3 h-3" /> Overlay hidden
                    </>
                  )}
                </button>
              </div>
            </div>
            <p className="text-[11px] text-stone-500 font-mono tracking-wide mb-4">
              Quotes converted to GBP at today&apos;s mid-market rate. Click a
              cell to mark the winning bid, Medicys&apos; proposal cost for that
              cell will use the supplier&apos;s price once the overlay is live.
            </p>

            <div className="bg-stone-50 border border-stone-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-stone-200 bg-white">
                      <th className="text-left p-3 font-mono uppercase tracking-wider text-stone-500 sticky left-0 bg-stone-100 z-10 min-w-[240px]">
                        Line item · country
                      </th>
                      {submittedRfqs.map((r) => (
                        <th
                          key={r.token}
                          className="text-left p-3 font-mono uppercase tracking-wider text-stone-500 min-w-[140px]"
                        >
                          {r.supplier_name}
                          {r.status === "drafted" && (
                            <span className="ml-1 text-amber-400">(draft)</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrixCells.map((cellKey) => {
                      // Split "p0:s0:r2:UK" → row_key="p0:s0:r2", country="UK"
                      const lastColon = cellKey.lastIndexOf(":");
                      const rowKey = cellKey.slice(0, lastColon);
                      const country = cellKey.slice(lastColon + 1);
                      const rowMeta = gridLookup[rowKey] || {};
                      const pickedToken = picks[cellKey] || null;
                      const defaultSample =
                        (rowMeta.default_samples || {})[country] ?? null;
                      return (
                        <tr
                          key={cellKey}
                          className="border-b border-stone-200"
                          data-testid={`matrix-row-${cellKey}`}
                        >
                          <td className="p-3 sticky left-0 bg-stone-100 z-10">
                            <p className="text-slate-900 text-xs">
                              <span className="font-medium">
                                {rowMeta.label || rowKey}
                              </span>
                              <span className="text-stone-500 ml-1.5">
                                · {country}
                              </span>
                            </p>
                            {rowMeta.block_label && (
                              <p className="text-[10px] font-mono text-stone-500 uppercase tracking-wider mt-0.5">
                                {rowMeta.block_label}
                                {defaultSample !== null && (
                                  <span className="ml-1.5 text-stone-400">
                                    spec n={defaultSample}
                                  </span>
                                )}
                              </p>
                            )}
                          </td>
                          {submittedRfqs.map((r) => {
                            const sub = r.submission || r.draft || {};
                            const perUnit = (sub.unit_overrides || {})[cellKey];
                            if (
                              perUnit === undefined ||
                              perUnit === "" ||
                              perUnit === null
                            ) {
                              return (
                                <td
                                  key={r.token}
                                  className="p-3 text-center text-stone-300"
                                >
                                 .
                                </td>
                              );
                            }
                            // Supplier's own quoted sample for this cell
                            //, if they reduced scope, this differs from
                            // the proposal default.
                            const supplierSample = (sub.sample_overrides || {})[cellKey];
                            const isReducedScope = sub.feasibility_status === "reduced_scope";
                            const effectiveSample =
                              isReducedScope && supplierSample !== undefined
                                ? Number(supplierSample)
                                : defaultSample;
                            const sampleMismatch =
                              isReducedScope &&
                              defaultSample !== null &&
                              effectiveSample !== defaultSample;
                            const isWinner = pickedToken === r.token;
                            const resolvedCell =
                              resolvedOverlay.resolved?.[cellKey];
                            const submissionCurrency =
                              sub.currency || r.currency || "GBP";
                            return (
                              <td
                                key={r.token}
                                onClick={() =>
                                  setPick(cellKey, isWinner ? null : r.token)
                                }
                                className={`p-3 cursor-pointer transition-colors ${
                                  isWinner
                                    ? "bg-emerald-500/10 hover:bg-emerald-500/15"
                                    : "hover:bg-white"
                                }`}
                                data-testid={`matrix-cell-${cellKey}-${r.token}`}
                              >
                                <div className="flex items-start gap-1.5">
                                  {isWinner && (
                                    <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0 mt-0.5" />
                                  )}
                                  <div>
                                    <p
                                      className={`tabular-nums ${
                                        isWinner
                                          ? "text-emerald-200 font-medium"
                                          : "text-slate-900"
                                      }`}
                                    >
                                      {submissionCurrency === "GBP"
                                        ? fmtGbp(Number(perUnit))
                                        : `${submissionCurrency} ${Number(
                                            perUnit
                                          ).toFixed(2)}`}
                                    </p>
                                    {isWinner &&
                                      resolvedCell &&
                                      submissionCurrency !== "GBP" && (
                                        <p className="text-[10px] text-emerald-400/70 font-mono mt-0.5">
                                          ≈ {fmtGbp(resolvedCell.per_unit_gbp)}
                                        </p>
                                      )}
                                    {!isWinner &&
                                      submissionCurrency !== "GBP" && (
                                        <p className="text-[10px] text-stone-500 font-mono mt-0.5">
                                          {submissionCurrency}
                                        </p>
                                      )}
                                    {sampleMismatch && (
                                      <p
                                        className="text-[10px] text-amber-300 font-mono mt-0.5"
                                        title="Supplier reduced the sample from the proposal default"
                                      >
                                        n={effectiveSample}{" "}
                                        <span className="text-amber-500/60">
                                          (was {defaultSample})
                                        </span>
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* QUICK LINK back to public proposal */}
        {proposal?.share_token && (
          <section className="text-center pt-6 border-t border-stone-200">
            <Link
              to={`/p/${proposal.share_token}`}
              className="inline-flex items-center gap-1.5 text-[11px] font-mono tracking-[0.14em] uppercase text-stone-500 hover:text-[#3EA8DC] transition-colors"
              data-testid="view-public-proposal"
            >
              <ExternalLink className="w-3 h-3" />
              View public proposal
            </Link>
          </section>
        )}
      </div>
    </StaffShell>
  );
}



/* ------------------------------------------------------------------ */
/* StaffGridEditor, preview & edit the supplier cost grid.           */
/* Renders the same table the supplier portal renders (sample /        */
/* per-unit / total columns per country) but with every field          */
/* editable inline + a delete button per row + a per-section + Add    */
/* line composer.                                                     */
/* ------------------------------------------------------------------ */
const CURRENCY_SYMBOL = {
  GBP: "£",
  EUR: "€",
  USD: "$",
  AUD: "$",
  CAD: "$",
  JPY: "¥",
  CHF: "CHF ",
};

function StaffGridEditor({
  phases,
  loading,
  error,
  onRetry,
  currency,
  updateRow,
  removeRow,
  addRow,
}) {
  const sym = CURRENCY_SYMBOL[currency] || `${currency} `;
  return (
    <div
      data-testid="grid-validation-panel"
      className="bg-stone-50/80 border border-[#3EA8DC]/20 rounded-xl p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#3EA8DC] mb-1">
            <ClipboardList className="w-3 h-3 inline -mt-0.5 mr-1" />
            Cost grid · this is exactly what the supplier will see
          </p>
          <p className="text-[11px] text-stone-500 leading-relaxed">
            Every field is editable. Adjust labels, samples, units and rates as needed. Delete rows with the trash icon, or use <strong>+ Add line</strong> under any section to ask for extras (hotel & travel, extra moderation hours, VAT, etc).
          </p>
        </div>
        {!loading && phases.length > 0 && (
          <button
            type="button"
            onClick={onRetry}
            data-testid="grid-reload"
            className="inline-flex items-center gap-1.5 text-[11px] font-mono tracking-[0.08em] uppercase text-stone-400 hover:text-[#3EA8DC] transition-colors"
            title="Reset to the latest auto-extracted grid"
          >
            <RefreshCw className="w-3 h-3" />
            Reset
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[11px] text-stone-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading the supplier cost grid…
        </div>
      )}
      {error && (
        <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {!loading && !error && phases.length === 0 && (
        <p className="text-[11px] text-stone-500 italic">
          Pick at least one country above to load the cost grid preview.
        </p>
      )}

      {phases.map((phase, pi) => (
        <PhaseEditTable
          key={`p${pi}`}
          phase={phase}
          phaseIndex={pi}
          sym={sym}
          currency={currency}
          updateRow={(si, ri, patch) => updateRow(pi, si, ri, patch)}
          removeRow={(si, ri) => removeRow(pi, si, ri)}
          addRow={(si, row) => addRow(pi, si, row)}
        />
      ))}
    </div>
  );
}

function PhaseEditTable({
  phase,
  phaseIndex,
  sym,
  currency,
  updateRow,
  removeRow,
  addRow,
}) {
  const countries = phase.countries || [];
  const [composerOpenFor, setComposerOpenFor] = useState(null);
  return (
    <div
      data-testid={`grid-phase-${phaseIndex}`}
      className="border border-stone-200 rounded-lg overflow-hidden"
    >
      <div className="bg-white px-3 py-2 flex items-center justify-between gap-3">
        <p className="text-[11px] font-mono tracking-[0.18em] uppercase text-slate-700 font-semibold">
          {phase.phase_name}
        </p>
        <p className="text-[10px] font-mono text-stone-500">
          {countries.join(" · ")}
        </p>
      </div>
      {(phase.sections || []).map((section, si) => {
        const sectionKey = `p${phaseIndex}:s${si}`;
        return (
          <div
            key={sectionKey}
            className="px-3 py-3 border-t border-stone-200 first:border-t-0"
          >
            <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-400 mb-2">
              {section.label}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left p-1.5 font-mono uppercase tracking-widest text-stone-500 text-[10px] border-b border-stone-200 w-1/3 min-w-[220px]">
                      Line item
                    </th>
                    {countries.map((c) => (
                      <th
                        key={c}
                        colSpan={3}
                        className="text-center p-1.5 font-mono uppercase tracking-widest text-stone-500 text-[10px] border-l border-stone-200 border-b border-stone-200"
                      >
                        {c}
                      </th>
                    ))}
                    <th className="border-b border-stone-200 w-8"></th>
                  </tr>
                  <tr>
                    <th className="border-b border-stone-200"></th>
                    {countries.map((c) => (
                      <Fragment key={c}>
                        <th className="text-right p-1.5 font-mono uppercase tracking-widest text-stone-400 text-[9px] border-l border-stone-200 border-b border-stone-200">
                          Sample
                        </th>
                        <th className="text-right p-1.5 font-mono uppercase tracking-widest text-stone-400 text-[9px] border-b border-stone-200">
                          Per-unit ({sym.trim()})
                        </th>
                        <th className="text-right p-1.5 font-mono uppercase tracking-widest text-stone-400 text-[9px] border-b border-stone-200">
                          Units
                        </th>
                      </Fragment>
                    ))}
                    <th className="border-b border-stone-200"></th>
                  </tr>
                </thead>
                <tbody>
                  {(section.rows || []).map((row, ri) => (
                    <StaffEditRow
                      key={row.row_key}
                      row={row}
                      countries={countries}
                      sym={sym}
                      colSpan={1 + 3 * countries.length}
                      onUpdate={(patch) => updateRow(si, ri, patch)}
                      onRemove={() => removeRow(si, ri)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {composerOpenFor === sectionKey ? (
              <CustomRowComposer
                onCancel={() => setComposerOpenFor(null)}
                onSave={(row) => {
                  addRow(si, row);
                  setComposerOpenFor(null);
                }}
                countries={countries}
                sectionKey={sectionKey}
                currency={currency}
              />
            ) : (
              <button
                type="button"
                onClick={() => setComposerOpenFor(sectionKey)}
                data-testid={`grid-add-line-${sectionKey}`}
                className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono tracking-[0.18em] uppercase text-emerald-300 hover:text-emerald-200 transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add line
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* Dispatch a row to its kind-specific editor. */
function StaffEditRow({ row, countries, sym, colSpan, onUpdate, onRemove }) {
  if (row.kind === "subgroup_header") {
    return (
      <StaffHeaderEditRow
        row={row}
        colSpan={colSpan}
        onUpdate={onUpdate}
        onRemove={onRemove}
      />
    );
  }
  if (row.kind === "sample") {
    return (
      <StaffSampleEditRow
        row={row}
        countries={countries}
        onUpdate={onUpdate}
        onRemove={onRemove}
      />
    );
  }
  if (row.kind === "lump_sum" || row.kind === "phase_fee") {
    return (
      <StaffLumpEditRow
        row={row}
        countries={countries}
        sym={sym}
        onUpdate={onUpdate}
        onRemove={onRemove}
      />
    );
  }
  // per_unit (default)
  return (
    <StaffPerUnitEditRow
      row={row}
      countries={countries}
      sym={sym}
      onUpdate={onUpdate}
      onRemove={onRemove}
    />
  );
}

const _editInputCls =
  "w-full bg-white border border-stone-200 rounded px-2 py-1 text-slate-900 text-xs focus:outline-none focus:border-[#3EA8DC]/60";
const _editInputRightCls =
  "w-full bg-white border border-stone-200 rounded px-2 py-1 text-slate-900 text-xs text-right tabular-nums focus:outline-none focus:border-[#3EA8DC]/60";

function RemoveCell({ onRemove, rowKey }) {
  return (
    <td className="p-1.5 align-middle text-right">
      <button
        type="button"
        onClick={onRemove}
        data-testid={`grid-remove-${rowKey}`}
        title="Remove this row"
        className="p-1 text-stone-500 hover:text-rose-300 transition-colors"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </td>
  );
}

function StaffHeaderEditRow({ row, colSpan, onUpdate, onRemove }) {
  return (
    <tr
      className="bg-stone-50"
      data-testid={`grid-row-${row.row_key}`}
    >
      <td colSpan={colSpan} className="px-1.5 py-1.5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5">
          <input
            type="text"
            value={row.block_label || row.label || ""}
            onChange={(e) =>
              onUpdate({ block_label: e.target.value, label: e.target.value })
            }
            data-testid={`grid-label-${row.row_key}`}
            placeholder="Group heading"
            className={`${_editInputCls} sm:max-w-xs font-semibold`}
          />
          <input
            type="text"
            value={row.block_subtitle || ""}
            onChange={(e) => onUpdate({ block_subtitle: e.target.value })}
            data-testid={`grid-subtitle-${row.row_key}`}
            placeholder="Subtitle (e.g. 60-min VDIs · Telephone)"
            className={`${_editInputCls} flex-1`}
          />
        </div>
      </td>
      <RemoveCell onRemove={onRemove} rowKey={row.row_key} />
    </tr>
  );
}

function StaffSampleEditRow({ row, countries, onUpdate, onRemove }) {
  return (
    <tr
      className="border-b border-stone-200 last:border-b-0 bg-sky-500/[0.04]"
      data-testid={`grid-row-${row.row_key}`}
    >
      <td className="p-1.5">
        <input
          type="text"
          value={row.label || ""}
          onChange={(e) => onUpdate({ label: e.target.value })}
          data-testid={`grid-label-${row.row_key}`}
          className={`${_editInputCls} italic`}
        />
      </td>
      {countries.map((c) => {
        const sample = (row.default_samples || {})[c] ?? 0;
        return (
          <td
            key={c}
            colSpan={3}
            className="p-1.5 align-middle border-l border-stone-200"
          >
            <div className="flex justify-end">
              <input
                type="number"
                min="0"
                step="1"
                value={sample}
                onChange={(e) => {
                  const n = e.target.value === ""
                    ? 0
                    : Number.parseInt(e.target.value, 10) || 0;
                  onUpdate({
                    default_samples: { ...(row.default_samples || {}), [c]: n },
                  });
                }}
                data-testid={`grid-sample-${row.row_key}-${c}`}
                className={`${_editInputRightCls} w-24`}
              />
            </div>
          </td>
        );
      })}
      <RemoveCell onRemove={onRemove} rowKey={row.row_key} />
    </tr>
  );
}

function StaffPerUnitEditRow({ row, countries, sym, onUpdate, onRemove }) {
  return (
    <tr
      className="border-b border-stone-200 last:border-b-0 hover:bg-stone-50"
      data-testid={`grid-row-${row.row_key}`}
    >
      <td className="p-1.5">
        <input
          type="text"
          value={row.label || ""}
          onChange={(e) => onUpdate({ label: e.target.value })}
          data-testid={`grid-label-${row.row_key}`}
          className={_editInputCls}
        />
      </td>
      {countries.map((c) => {
        const sample = (row.default_samples || {})[c] ?? 0;
        const units = (row.default_units || {})[c] ?? 0;
        return (
          <Fragment key={c}>
            <td className="p-1.5 align-middle border-l border-stone-200">
              <input
                type="number"
                min="0"
                step="1"
                value={sample}
                onChange={(e) => {
                  const n = e.target.value === ""
                    ? 0
                    : Number.parseInt(e.target.value, 10) || 0;
                  // Keep units in sync when staff edits sample, unless
                  // staff has explicitly diverged units already.
                  const samePrev = sample === units;
                  onUpdate({
                    default_samples: { ...(row.default_samples || {}), [c]: n },
                    ...(samePrev
                      ? {
                          default_units: {
                            ...(row.default_units || {}),
                            [c]: n,
                          },
                        }
                      : {}),
                  });
                }}
                data-testid={`grid-sample-${row.row_key}-${c}`}
                className={_editInputRightCls}
              />
            </td>
            <td className="p-1.5 align-middle">
              <div className="bg-stone-500/5 border border-dashed border-stone-500/30 rounded px-2 py-1 text-stone-500 text-[10px] text-center font-mono tracking-wider">
                supplier
              </div>
            </td>
            <td className="p-1.5 align-middle">
              <input
                type="number"
                min="0"
                step="1"
                value={units}
                onChange={(e) => {
                  const n = e.target.value === ""
                    ? 0
                    : Number.parseInt(e.target.value, 10) || 0;
                  onUpdate({
                    default_units: { ...(row.default_units || {}), [c]: n },
                  });
                }}
                data-testid={`grid-units-${row.row_key}-${c}`}
                placeholder="units"
                title="Quantity multiplier the supplier's per-unit price will scale with (defaults to sample)."
                className={_editInputRightCls}
              />
            </td>
          </Fragment>
        );
      })}
      <RemoveCell onRemove={onRemove} rowKey={row.row_key} />
    </tr>
  );
}

function StaffLumpEditRow({ row, countries, sym, onUpdate, onRemove }) {
  const isFee = row.kind === "phase_fee";
  return (
    <tr
      className="border-b border-stone-200 last:border-b-0 hover:bg-stone-50"
      data-testid={`grid-row-${row.row_key}`}
    >
      <td className="p-1.5">
        <div className="flex flex-col gap-1.5">
          <input
            type="text"
            value={row.label || ""}
            onChange={(e) => onUpdate({ label: e.target.value })}
            data-testid={`grid-label-${row.row_key}`}
            className={_editInputCls}
          />
          {isFee && (
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min="0"
                step="0.01"
                value={
                  row.caption_rate == null
                    ? ""
                    : Number((row.caption_rate * 100).toFixed(2))
                }
                onChange={(e) => {
                  const pct = Number.parseFloat(e.target.value);
                  onUpdate({
                    caption_rate: Number.isFinite(pct) ? pct / 100 : null,
                  });
                }}
                data-testid={`grid-rate-${row.row_key}`}
                placeholder="optional suggested %"
                className={`${_editInputRightCls} w-28`}
              />
              <span className="text-[10px] font-mono text-stone-500">
                %
              </span>
              <input
                type="text"
                value={row.subtitle || ""}
                onChange={(e) => onUpdate({ subtitle: e.target.value })}
                data-testid={`grid-subtitle-${row.row_key}`}
                placeholder="optional caption to suggest a rate"
                className={`${_editInputCls} flex-1`}
              />
            </div>
          )}
        </div>
      </td>
      {countries.map((c) => (
        <Fragment key={c}>
          {/* Sample column, N/A for lump_sum / phase_fee */}
          <td className="p-1.5 align-middle border-l border-stone-200 text-center text-stone-400 text-[10px] font-mono">
           .
          </td>
          {/* Per-unit column, supplier-only input. We deliberately
              do NOT carry our internal price here; the supplier
              enters their lump cost without anchoring. */}
          <td className="p-1.5 align-middle">
            <div className="bg-stone-500/5 border border-dashed border-stone-500/30 rounded px-2 py-1 text-stone-500 text-[10px] text-center font-mono tracking-wider">
              supplier
            </div>
          </td>
          {/* Units column, N/A for lump_sum / phase_fee (units=1). */}
          <td className="p-1.5 align-middle text-center text-stone-400 text-[10px] font-mono">
           .
          </td>
        </Fragment>
      ))}
      <RemoveCell onRemove={onRemove} rowKey={row.row_key} />
    </tr>
  );
}


/* Inline composer for a custom row. Three kinds:
   - lump_sum: just a label + countries
   - per_unit: label + sample per country (units = sample by default)
   - phase_fee: label + default rate (%) + caption subtitle. */
function CustomRowComposer({ onCancel, onSave, countries, sectionKey, currency }) {
  const [kind, setKind] = useState("lump_sum");
  const [label, setLabel] = useState("");
  // Per-unit + phase_fee: per-country sample (lump_sum ignores this).
  const [samples, setSamples] = useState(() =>
    Object.fromEntries(countries.map((c) => [c, ""]))
  );
  const [rate, setRate] = useState(""); // For phase_fee, %, e.g. "20" → 0.20
  const [subtitle, setSubtitle] = useState(""); // Optional caption

  const canSave = label.trim().length > 0;

  const save = () => {
    if (!canSave) return;
    const trimmedLabel = label.trim();
    const rowKey = `custom:${sectionKey}:${Date.now().toString(36)}${Math.random()
      .toString(36)
      .slice(2, 6)}`;
    const row = {
      row_key: rowKey,
      kind,
      label: trimmedLabel,
      block_id: "",
      block_label: "",
      block_subtitle: "",
    };
    if (kind === "per_unit") {
      const parsedSamples = {};
      const parsedUnits = {};
      countries.forEach((c) => {
        const n = Number.parseFloat(samples[c]);
        const s = Number.isFinite(n) && n > 0 ? n : 0;
        parsedSamples[c] = s;
        parsedUnits[c] = s; // default units = sample
      });
      row.default_samples = parsedSamples;
      row.default_units = parsedUnits;
    } else if (kind === "phase_fee") {
      const parsedRate = Number.parseFloat(rate);
      row.caption_rate = Number.isFinite(parsedRate)
        ? parsedRate / 100
        : null;
      row.subtitle = subtitle.trim() || (rate ? `${rate}% of` : "");
      row.caption_base = Object.fromEntries(countries.map((c) => [c, 0]));
      row.default_by_country = Object.fromEntries(
        countries.map((c) => [c, 0])
      );
      row.default_samples = Object.fromEntries(countries.map((c) => [c, 1]));
      row.default_units = Object.fromEntries(countries.map((c) => [c, 1]));
    } else {
      // lump_sum
      row.default_by_country = Object.fromEntries(
        countries.map((c) => [c, 0])
      );
      row.default_samples = Object.fromEntries(countries.map((c) => [c, 1]));
      row.default_units = Object.fromEntries(countries.map((c) => [c, 1]));
    }
    onSave(row);
  };

  return (
    <div
      data-testid={`grid-composer-${sectionKey}`}
      className="mt-3 bg-stone-100 border border-emerald-500/20 rounded-lg p-3 space-y-3"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-emerald-300">
          New custom line · {sectionKey}
        </p>
        <button
          type="button"
          onClick={onCancel}
          data-testid={`grid-composer-cancel-${sectionKey}`}
          className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 hover:text-slate-700"
        >
          Cancel
        </button>
      </div>

      {/* Kind picker */}
      <div className="flex flex-wrap gap-1.5">
        {[
          { key: "lump_sum", label: "Lump sum" },
          { key: "per_unit", label: "Per-unit" },
          { key: "phase_fee", label: "% Phase fee" },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setKind(opt.key)}
            data-testid={`grid-composer-kind-${opt.key}`}
            className={`px-2.5 py-1 rounded text-[10px] font-mono tracking-[0.12em] uppercase transition-colors ${
              kind === opt.key
                ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"
                : "bg-white text-stone-400 border border-stone-200 hover:text-slate-700"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Label */}
      <div>
        <label className="block text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-1">
          Line label
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={
            kind === "lump_sum"
              ? "e.g. Hotel & travel"
              : kind === "per_unit"
              ? "e.g. Extra interview hour"
              : "e.g. VAT 20%"
          }
          data-testid={`grid-composer-label-${sectionKey}`}
          className="w-full bg-white border border-stone-200 rounded-md px-2.5 py-1.5 text-xs text-slate-900 focus:outline-none focus:border-emerald-500/60"
        />
      </div>

      {/* Per-unit: sample inputs per country */}
      {kind === "per_unit" && (
        <div>
          <label className="block text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-1">
            Sample / units per country
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {countries.map((c) => (
              <div key={c} className="flex items-center gap-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-stone-400 w-12 shrink-0">
                  {c}
                </span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={samples[c] ?? ""}
                  onChange={(e) =>
                    setSamples((s) => ({ ...s, [c]: e.target.value }))
                  }
                  placeholder="0"
                  data-testid={`grid-composer-sample-${sectionKey}-${c}`}
                  className="w-full bg-white border border-stone-200 rounded-md px-2 py-1 text-xs text-slate-900 text-right tabular-nums focus:outline-none focus:border-emerald-500/60"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase fee: rate + caption */}
      {kind === "phase_fee" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-1">
              Default rate (%)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="e.g. 20"
              data-testid={`grid-composer-rate-${sectionKey}`}
              className="w-full bg-white border border-stone-200 rounded-md px-2.5 py-1.5 text-xs text-slate-900 text-right tabular-nums focus:outline-none focus:border-emerald-500/60"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-1">
              Caption (optional)
            </label>
            <input
              type="text"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="e.g. 20% of total"
              data-testid={`grid-composer-subtitle-${sectionKey}`}
              className="w-full bg-white border border-stone-200 rounded-md px-2.5 py-1.5 text-xs text-slate-900 focus:outline-none focus:border-emerald-500/60"
            />
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          data-testid={`grid-composer-save-${sectionKey}`}
          className="inline-flex items-center gap-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-mono tracking-[0.1em] uppercase px-3 py-1.5 rounded-md transition-colors border border-emerald-500/40"
        >
          <Check className="w-3 h-3" />
          Add line ({currency})
        </button>
      </div>
    </div>
  );
}
